import { useMutationWrapper, showErrorDialog } from "@SeemplicityDev/shared";
import { CREATE_AUTOMATION, EDIT_AUTOMATION } from "../../AutomationsAPI";
import {
  GET_FINDING_FILTERS,
  SAVE_FILTER,
} from "../../../Findings/FindingsFilters/FindingsFilterApi";
import {
  CreateAutomationVariables,
  EditAutomationVariables,
  AutomationTypeEnum,
  AUTOMATION_TRIGGER_TYPES,
  AutomationFormBaseFields,
} from "../../AutomationsUtils";
import { ApolloError } from "@apollo/client";
import { showToast } from "../../../Forms/Dialogs/Toast/Toast";
import { useCallback } from "react";
import useSavedFilterType from "../../../AppView/FilterManagement/useSavedFilterType";
import { filterEntityEnum } from "../../../AppView/FilterManagement/filterEntity";

interface AutomationFormFields extends AutomationFormBaseFields {
  params: Record<string, any>;
}

type onSuccessType = (automationId: string, automationName: string) => void;

type onSubmitType = (
  data: AutomationFormFields,
  onSuccess?: onSuccessType,
  shouldShowSuccessToast?: boolean
) => void;

interface useAutomationSubmitOptions {
  automationId?: string;
  automationType: AutomationTypeEnum;
}

interface useAutomationSubmitReturn {
  onSubmit: onSubmitType;
  createAutomationLoading: boolean;
  editAutomationLoading: boolean;
}

const useAutomationSubmit = ({
  automationType,
  automationId,
}: useAutomationSubmitOptions): useAutomationSubmitReturn => {
  const savedFilterType = useSavedFilterType(filterEntityEnum.REMEDIATION_ITEM);

  const [createAutomation, { loading: createAutomationLoading }] =
    useMutationWrapper(CREATE_AUTOMATION) as unknown as [
      (options: any) => Promise<any>,
      { loading: boolean },
    ];

  const [editAutomation, { loading: editAutomationLoading }] =
    useMutationWrapper(EDIT_AUTOMATION, {
      refetchQueries: ["getAutomation"],
    }) as unknown as [(options: any) => Promise<any>, { loading: boolean }];

  // Document form: `filters` is not mounted here, so the name form is a no-op.
  const [saveFilter] = useMutationWrapper(SAVE_FILTER, {
    refetchQueries: [GET_FINDING_FILTERS],
  }) as unknown as [(options: any) => Promise<any>];

  const onEditAutomation = (
    variables: EditAutomationVariables,
    onSuccess: (automationId: string, automationName: string) => void,
    shouldShowSuccessToast: boolean
  ) => {
    editAutomation({
      variables,
    })
      .then(() => {
        if (shouldShowSuccessToast) {
          showToast({
            message: (
              <div className={"modal-text"}>
                <span className={"modal-live-text"}>{variables?.name}</span> was
                updated successfully
              </div>
            ) as any,
            "data-testid": "automation-updated",
          });
        }
        onSuccess?.(variables.id, variables.name);
      })
      .catch((err: ApolloError) => {
        showErrorDialog({ message: err?.message });
      });
  };

  const onCreateAutomation = (
    variables: CreateAutomationVariables,
    onSuccess: onSuccessType,
    shouldShowSuccessToast: boolean
  ) => {
    createAutomation({
      variables,
    })
      .then((res) => {
        if (shouldShowSuccessToast) {
          showToast({
            message: (
              <div className={"modal-text"}>
                <span className={"modal-live-text"}>{variables?.name}</span> was
                created successfully
              </div>
            ) as any,
            "data-testid": "automation-created",
          });
        }

        onSuccess?.(
          res.data?.create_automation?.automation?.id,
          variables.name
        );
      })
      .catch((err: ApolloError) => {
        showErrorDialog({ message: err?.message });
      });
  };

  const onSubmit: onSubmitType = useCallback(
    (data, onSuccess = () => {}, shouldShowSuccessToast = true) => {
      const filtersid = data.filter?.value?.id;
      const scopesid = data.scope?.value?.id;

      const variables = {
        name: data.name,
        trigger: {
          type: AUTOMATION_TRIGGER_TYPES.CRON,
          value: data.schedule,
        },
        action: {
          type: automationType,
          params: JSON.stringify(data.params),
        },
      };

      if (!!automationId) {
        onEditAutomation(
          {
            id: automationId,
            ...variables,
            filters_config: {
              filtersid,
              scopesid,
            },
          },
          onSuccess,
          shouldShowSuccessToast
        );
      } else {
        if (!filtersid) {
          saveFilter({
            variables: {
              name: data.filter?.label,
              filterData: data.filter?.value?.query,
              filterType: savedFilterType,
            },
          })
            .then((res) => {
              onCreateAutomation(
                {
                  ...variables,
                  filters_config: {
                    filtersid:
                      res.data?.new_finding_filter_v2?.finding_filter?.id,
                    scopesid,
                  },
                },
                onSuccess,
                shouldShowSuccessToast
              );
            })
            .catch((err: ApolloError) => {
              showErrorDialog({ message: err?.message });
            });

          return;
        }

        onCreateAutomation(
          {
            ...variables,
            filters_config: {
              filtersid,
              scopesid,
            },
          },
          onSuccess,
          shouldShowSuccessToast
        );
      }
    },
    [automationId, savedFilterType]
  );

  return { onSubmit, createAutomationLoading, editAutomationLoading };
};

export default useAutomationSubmit;
